<template>
  <Grid
    class="rc-rfr-contacts-grid"
    :pageable="pageable"
    :data-items="contacts"
    :skip="skip"
    :take="take"
    :page-size="take"
    :total="total"
    :columns="columns"
    :loader="loading"
    :filter="filter"
    :column-menu="!loading"
    :sort="sort"
    :resizable="true"
    :reorderable="true"
    @columnresize="onColumnResize"
    @columnreorder="onColumnReorder"
    @filterchange="filterChangeHandler"
    @pagechange="pageChangeHandler"
    @sortchange="sortChangeHandler"
    data-cy="rc-rfr-contacts-grid"
  >
    <template v-slot:columnMenu="{ props }">
      <ColumnMenu
        :column="props.column"
        :filterable="props.filterable"
        :filter="props.filter"
        :sortable="props.sortable"
        :sort="props.sort"
        :columns="columns"
        :getItems="getPropValues"
        @sortchange="(e) => props.onSortchange(e)"
        @filterchange="(e) => props.onFilterchange(e)"
        @closemenu="(e) => props.onClosemenu(e)"
        @contentfocus="(e) => props.onContentfocus(e)"
        @columnssubmit="onColumnsSubmit"
      />
    </template>
    <template v-slot:StatusCell="{ props: { dataItem, field } }">
      <td class="k-table-td">
        {{ EnumPersonJobStatus[dataItem[field]] }}
      </td>
    </template>
  </Grid>
</template>

<script setup lang="ts">
import {
  Grid,
  type GridColumnProps,
  type GridColumnReorderEvent,
  type GridColumnResizeEvent,
  type GridFilterChangeEvent,
  type GridPageChangeEvent,
  type GridPagerSettings,
} from "@progress/kendo-vue-grid";
import { ref, watchEffect } from "vue";
import type {
  CompositeFilterDescriptor,
  SortDescriptor,
} from "@progress/kendo-data-query";
import ColumnMenu from "@/shared/ui/inputs/ColumnMenu.vue";

import type {
  ItemChangedOptions,
  ItemSortChangedOptions,
} from "@/Domain/ItemChangedOptions";
import type { GridColumnPropsExtended } from "@/Domain/GridColumnPropsExtended";
import { EnumPersonJobStatus } from "@/Domain/EnumPersonJobStatus";
import { GridOptionsService } from "@/shared/services/GridOptionsService";
import { useNotificationStore } from "@/stores/useNotificationStore";
import { GridMenuActionType } from "@/Domain/GridMenuActionType";
import { NotificationTypeEnum } from "@/Domain/NotificationTypeEnum";
import type { Contact } from "@/Domain/Contact";

const notificationStore = useNotificationStore();

const props = defineProps<{
  contacts: Contact[];
  loading: boolean;
  skip: number;
  pageSize: number;
  total: number;
  filter: CompositeFilterDescriptor;
  sort: SortDescriptor[];
  getPropValues: (column: GridColumnPropsExtended) => Promise<Contact[]>;
}>();

const emit = defineEmits<{
  (e: "contactsChanged", options: ItemChangedOptions): void;
  (e: "sortChanged", options: ItemSortChangedOptions): void;
  (e: "columnreorder", event: GridColumnReorderEvent): void;
  (e: "columnssubmit", columnsProps: GridColumnProps[]): void;
}>();

const gridName = "RcRfrContactsGrid";

const skip = ref<number>(props.skip);
const take = ref<number>(props.pageSize);

const columns = ref<GridColumnPropsExtended[]>([
  {
    title: "Role",
    field: "Title",
    columnMenu: "columnMenu",
  },
  {
    title: "Name",
    field: "FullName",
    columnMenu: "columnMenu",
    hideInColumnMenu: true,
  },
  {
    title: "Status",
    field: "Status",
    cell: "StatusCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Phone",
    field: "Voice",
    columnMenu: "columnMenu",
  },
  {
    title: "Email",
    field: "Email",
    columnMenu: "columnMenu",
  },
  {
    title: "Cell",
    field: "Cell",
    columnMenu: "columnMenu",
  },
  {
    title: "Fax",
    field: "Fax",
    columnMenu: "columnMenu",
  },
  {
    title: "Home Phone",
    field: "HomeVoice",
    columnMenu: "columnMenu",
  },
  {
    title: "Home Email",
    field: "HomeEmail",
    columnMenu: "columnMenu",
  },
  {
    title: "Home Cell",
    field: "HomeCell",
    columnMenu: "columnMenu",
  },
  {
    title: "Home Fax",
    field: "HomeFax",
    columnMenu: "columnMenu",
  },
]);

let pageSizes = [10, 20, 50, 100];
if (!pageSizes.includes(take.value)) pageSizes.push(take.value);
pageSizes = pageSizes.sort((a, b) => a - b);
const pageable = ref<GridPagerSettings>({
  buttonCount: 5,
  info: true,
  type: "numeric",
  pageSizes: pageSizes,
  previousNext: true,
});

GridOptionsService.loadGridOptions(gridName, columns.value);

const filterChangeHandler = function (e: GridFilterChangeEvent) {
  skip.value = 0;
  emit("contactsChanged", { skip: 0, take: take.value, filterEvent: e });
};

const sortChangeHandler = function (e: any) {
  const descriptors = e.sort as SortDescriptor[];
  if (!descriptors) return;
  skip.value = 0;
  emit("sortChanged", {
    field: descriptors[0].field,
    ascending: descriptors[0].dir === "asc",
  });
};

const pageChangeHandler = function (event: GridPageChangeEvent) {
  skip.value = event.page.skip;
  take.value = event.page.take;
  emit("contactsChanged", {
    skip: event.page.skip,
    take: event.page.take,
    filterEvent: null,
  });
};

const onColumnsSubmit = function (columnsProps: GridColumnProps[]) {
  if (columnsProps) {
    columns.value = columnsProps;
    GridOptionsService.saveVisibleColumns(gridName, columnsProps);
  }
  console.debug("RcRfrContactsGrid.vue, onColumnSubmit", columnsProps);

  emit("columnssubmit", columnsProps);
};

const onColumnResize = function (e: GridColumnResizeEvent) {
  const col = GridOptionsService.getResizedColumn(e, columns.value);
  if (col.title)
    GridOptionsService.saveColumnWidth(gridName, col.title, e.newWidth);
};

const onColumnReorder = function (e: GridColumnReorderEvent) {
  GridOptionsService.setSelectDetailsColumnOrder(e);
  columns.value = e.columns;
  GridOptionsService.saveColumnsOrder(gridName, e.columns);
  emit("columnreorder", e);
};

const resetColumnSelection = function () {
  columns.value.forEach((col) => {
    col.hidden = col.defaultHiddenValue ?? false;
  });
  GridOptionsService.clearVisibleColumns(gridName);
};

const resetColumnSizes = function (showNotification = true) {
  GridOptionsService.clearColumnWidths(gridName);
  if (showNotification) {
    notificationStore.notify({
      type: NotificationTypeEnum.success,
      message:
        "Column sizes reset successfully. Please refresh the page to see the changes.",
      duration: 5000,
    });
  }
};

const resetColumnOrder = function () {
  columns.value.forEach((col) => {
    col.orderIndex = undefined;
  });
  GridOptionsService.clearColumnsOrder(gridName);
};

const resetAllSettings = function () {
  resetColumnSelection();
  resetColumnSizes(false);
  resetColumnOrder();

  notificationStore.notify({
    type: NotificationTypeEnum.success,
    message:
      "All settings reset successfully. Please refresh the page to see the changes.",
    duration: 5000,
  });
};

const executeAction = function (actionType: GridMenuActionType) {
  switch (actionType) {
    case GridMenuActionType.ResetColumnSelection:
      resetColumnSelection();
      break;
    case GridMenuActionType.ResetColumnSizes:
      resetColumnSizes();
      break;
    case GridMenuActionType.ResetColumnOrder:
      resetColumnOrder();
      break;
    case GridMenuActionType.ResetAllSettings:
      resetAllSettings();
      break;
  }
};

defineExpose({
  executeAction,
});

watchEffect(() => {
  skip.value = props.skip;
});

watchEffect(() => {
  take.value = props.pageSize;
});
</script>

<style scoped lang="scss">
.rc-rfr-contacts-grid {
  min-height: 450px;
}
</style>
